import React, { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';
import { useToasts } from 'react-toast-notifications';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import IconCheckmark from '@components/common/IconCheckmark/IconCheckmark';
import ConfirmModal from '@components/common/Modals/ConfirmModal/ConfirmModal';
import Pagination from '@components/common/Pagination/Pagination';
import Spinner from '@components/common/Spinner/Spinner';
import AddMessageForm from '@components/lpc/Pot/MainModule/Activity/Notification/AddMessageForm/AddMessageForm';
import NotificationCard from '@components/lpc/Pot/MainModule/Activity/Notification/NotificationCard/NotificationCard';
import { personRoles } from '@models/PotModel/personRoles';
import notificationService from '@services/domain/Lpc/NotificationsService';
import { getRoute, ROUTE } from '@services/http/Route';
import { getUserAvatarSrc } from '@services/utils/UserService';

import css from './Notification.module.scss';

type NotificationProps = {
    potRef: string;
    personRole: string;
    category: string;
    isOffered: boolean;
};

const Notification = ({ potRef, personRole, category, isOffered }: NotificationProps) => {
    const { addToast } = useToasts();
    const { t } = useTranslateLpc(['lpc-pot', 'common']);
    const router = useRouter();

    const [isLoading, setIsLoading] = useState(false);
    const [notificationList, setNotificationList] = useState([]);
    const [notificationNumber, setNotificationNumber] = useState<number>(0);
    const [pageRangeDisplayed, setPageRangeDisplayed] = useState<number>(2);
    const [cursor, setCursor] = useState<number>(0);
    const [pageCount, setPageCount] = useState<number>(0);
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
    const [isLoginModalVisible, setIsLoginModalVisible] = useState<boolean>(false);
    const itemPerPage: number = 2;

    const fetchAllNotifications = useCallback(() => {
        setIsLoading(true);
        notificationService
            .getAllActivities(potRef, cursor, itemPerPage)
            .then((res) => {
                setNotificationNumber(res.totalCount);
                setNotificationList(res.potActivities);
            })
            .catch((err) =>
                addToast(t(`errors.${err.code}`), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            )
            .finally(() => setIsLoading(false));
    }, [cursor, itemPerPage, potRef]);

    useEffect(() => {
        itemPerPage && fetchAllNotifications();
    }, [itemPerPage, cursor]);

    useEffect(() => {
        setCursor(0);
    }, [itemPerPage]);

    useEffect(() => {
        if (notificationNumber > 0) {
            setPageCount(Math.ceil(notificationNumber / itemPerPage));
        }
    }, [notificationNumber, itemPerPage]);

    const handlePageClick = (page) => {
        setPageRangeDisplayed(2);
        const pageSelected = page.selected;
        const offset = Math.ceil(pageSelected * itemPerPage);
        setCursor(offset);
        isFirstRender && setIsFirstRender(false);
    };

    const onUpdateActivities = () => {
        setCursor(0);
        fetchAllNotifications();
    };

    return (
        <>
            <ConfirmModal
                onHide={() => setIsLoginModalVisible(false)}
                onConfirm={() => router.push(getRoute(ROUTE.LPC.AUTH.SIGN_IN, `?source=`, router.asPath))}
                title={t('activity.modal.login-title')}
                text={t('activity.modal.login-text')}
                confirmLabel={t('activity.modal.login-confirm')}
                isVisible={isLoginModalVisible}
                iconComponent={<IconCheckmark variant={'spy'} />}
            />

            {(personRole == personRoles.ORGANISER || notificationList.length > 0) && (
                <div className={css.notification} id={'notifContainer'}>
                    {personRole == personRoles.ORGANISER && !isOffered && (
                        <>
                            <h5>
                                {category === 'BEREAVEMENT' || category === 'SOLIDARITY_POOL'
                                    ? t('activity.activity_donor')
                                    : t('activity.activity_participant')}{' '}
                                {notificationNumber > 0 && (
                                    <span className={css.notification__number}>({notificationNumber})</span>
                                )}
                            </h5>
                            <div className={css.notification__form}>
                                <AddMessageForm onUpdateActivities={onUpdateActivities} potRef={potRef} />
                            </div>
                        </>
                    )}
                    {notificationList.length > 0 && (
                        <>
                            <div className={css.notification__flex}>
                                {isLoading ? (
                                    <Spinner customClass={css.notification__spinner} />
                                ) : (
                                    <>
                                        {notificationList.map((item) => (
                                            <NotificationCard
                                                inviteID={null}
                                                orderId={item.participant.orderId}
                                                message={item.participant.message}
                                                hideName={item.participant.hideName}
                                                hideContribution={item.participant.hideContribution}
                                                contribution={item.participant.contribution}
                                                onUpdateActivities={onUpdateActivities}
                                                key={item.uniq}
                                                id={item.uniq}
                                                avatar={getUserAvatarSrc(item.participant)}
                                                firstName={item.participant.firstName}
                                                lastName={item.participant.lastName}
                                                creationDate={item.date}
                                                type={item.type}
                                                isOrganiser={item.isOrganiser}
                                                likesCount={item.likesCount}
                                                isLiked={item.isLiked}
                                                personRole={personRole}
                                                potRef={potRef}
                                            />
                                        ))}
                                    </>
                                )}
                            </div>
                            <Pagination
                                handlePageClick={handlePageClick}
                                pageCount={pageCount}
                                pageRangeDisplayed={pageRangeDisplayed}
                                marginPagesDisplayed={1}
                            />
                        </>
                    )}
                </div>
            )}
        </>
    );
};

export default Notification;
